/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { RichTextRenderer } from "@contember/react-client";
import {
	FeatureCategoryType,
	FeatureValueType,
	ProductFlagValue,
} from "@granlupo/api-client";
import slugify from "@sindresorhus/slugify";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import thumbDown from "../../../public/thumpDown.svg";
import thumbUp from "../../../public/thumpUp.svg";
import { filterNonEmpty } from "../../../utils/filterNonEmpty";
import { getExcludedAllergens } from "../../const/AllergenHelper";
import { selectFlavors } from "../../const/FlavorHelper";
import { InternalLink } from "../../const/Links";
import { rephraseSize } from "../../const/Rephrase";
import { useTranslate } from "../../contexts/TranslationContext";
import type {
	ContentBlockResult,
	ContentReferenceResult,
} from "../../data/ContentFragment";
import type { ImageResult } from "../../data/ImageFragment";
import type { ItemSubCategoryResult } from "../../data/ItemSubCategoryFragment";
import type { ProductFeatureResult } from "../../data/ProductFeatureFragment";
import { useDER } from "../../user/ActiveDogContext";
import { useGallery } from "../../utils/useGallery";
import { ProductBanner } from "../product/ProductBanner";
import { AccordionList } from "../shared/AccordionList";
import { ArticleBanner } from "../shared/ArticleBanner";
import { BrandBanner } from "../shared/BrandBanner";
import { BreakableContent } from "../shared/BreakableContent";
import { ContactFormMessageModal } from "../shared/ContactFormMessageModal";
import { Container } from "../shared/Container";
import { ContentOutline } from "../shared/ContentOutline";
import { IconItemGroup } from "../shared/IconItemGroup";
import { ManufacturerArticle } from "../shared/ManufacturerArticle";
import { ProductLineCardGroup } from "../shared/ProductLineCardGroup";
import { SummaryArticle } from "../shared/SummaryArticle";
import { SummaryList } from "../shared/SummaryList";
import { TagRow } from "../shared/TagRow";
import { RegisterBanner } from "../static/RegisterBanner";
import styles from "./Content.module.sass";
import { Fact } from "./Fact";
import { Gallery } from "./Gallery";
import { ImageWrapper } from "./ImageWrapper";
import { Quote } from "./Quote";
import { ensureAllReferences } from "./utils";

export type AnchorElement = {
	type: "anchor";
	href: string;
};

export type CustomElement = AnchorElement;

export function Content(props: {
	blocks: ContentBlockResult[];
	allAllergens?: ItemSubCategoryResult[];
	openModal?: () => void;
}) {
	const translate = useTranslate();
	const router = useRouter();
	const [isContactModalOpen, setIsContactModalOpen] = useState(false);

	const routerRef = useRef(router);
	routerRef.current = router;

	const getDailyEnergyRequirement = useDER();

	const dogDailyEnergyRequirementNumber = Number(
		getDailyEnergyRequirement?.data?.dailyEnergyRequirement
	);

	const media = props.blocks
		.map((block) => block.references)
		.map((reference) =>
			reference.filter((ref) => Boolean(ref.image?.image) && !ref.image?.link)
		);

	const flattenMedia = media
		.reduce((a, b) => a.concat(b), [])
		.map((ref) => ref.image)
		.filter(filterNonEmpty);

	const lightBox = useGallery(flattenMedia);
	const nestedTypes = useMemo(
		() => [
			"listItem",
			"anchor",
			"tableCell",
			"tableRow",
			"scrollTarget",
			"link",
		],
		[]
	);
	const referenceRenderers = useMemo(
		() =>
			ensureAllReferences({
				fact(ref) {
					return (
						<Container size="narrow" disableGutters>
							<Fact>
								{ref.reference.fact && (
									<BreakableContent text={ref.reference.fact} />
								)}
							</Fact>
						</Container>
					);
				},
				link(ref) {
					return (
						ref.reference.link?.externalLink && (
							<Link
								href={ref.reference.link.externalLink}
								title={ref.reference.link.externalLink}
							/>
						)
					);
				},
				outline(ref) {
					return <ContentOutline items={ref.reference.outline?.items} />;
				},
				gallery(ref) {
					return (
						<Gallery
							id={ref.reference.id}
							items={
								ref.reference.images.map(
									(image) => image.image
								) as ImageResult[]
							}
						/>
					);
				},
				clear() {
					return <div style={{ clear: "both" }} />;
				},
				hr() {
					return (
						<div>
							<hr />
						</div>
					);
				},
				image(ref) {
					return (
						ref.reference.image && (
							<ImageWrapper
								image={ref.reference.image}
								openGallery={() =>
									lightBox?.open(
										flattenMedia.indexOf(ref.reference.image ?? flattenMedia[0])
									)
								}
							/>
						)
					);
				},
				video(ref) {
					return <h2>{ref.referenceType}</h2>;
				},
				quote(ref) {
					return (
						<Container size="narrow" disableGutters>
							<Quote
								primary={ref.reference.primaryText}
								secondary={ref.reference.secondaryText}
							/>
						</Container>
					);
				},
				relatedBlogPost(ref) {
					return (
						<ArticleBanner
							isEmbedded
							title={ref.reference.relatedBlogPost?.title ?? "Článek"}
							buttonLabel={translate("listingBlogPosts.button")}
							lead={ref.reference.relatedBlogPost?.lead}
							href={`${InternalLink.BlogPost}${ref.reference.relatedBlogPost?.slug}`}
							imageUrl={ref.reference.relatedBlogPost?.image?.url}
							tagContent={
								(ref.reference.relatedBlogPost?.tags &&
									ref.reference.relatedBlogPost?.tags?.length > 0) ||
								(ref.reference.relatedBlogPost?.blogPostTags &&
									ref.reference.relatedBlogPost?.blogPostTags?.length > 0) ? (
									<TagRow
										tags={ref.reference.relatedBlogPost?.blogPostTags.concat(
											ref.reference.relatedBlogPost?.tags
										)}
									/>
								) : undefined
							}
						/>
					);
				},
				relatedProduct(ref) {
					if (ref.reference.relatedProduct) {
						console.log(
							JSON.stringify(ref.reference.relatedProduct.evaluation, null, 2)
						);
					}
					return (
						ref.reference.relatedProduct &&
						props.allAllergens && (
							<>
								<ProductBanner
									badge={ref.reference.badge}
									badgeColor={ref.reference.badgeColor}
									isEmbedded
									noBottomBorder
									openModal={props.openModal}
									dogDailyEnergyRequirement={dogDailyEnergyRequirementNumber}
									product={ref.reference.relatedProduct}
									excludedAllergens={getExcludedAllergens(
										props.allAllergens,
										ref.reference.relatedProduct
									)}
								/>
							</>
						)
					);
				},
				relatedIngredient(ref) {
					return (
						<ArticleBanner
							isEmbedded
							title={ref.reference.relatedIngredient?.name ?? "Složka"}
							buttonLabel={translate("listingIngredients.button.readMore")}
							lead={ref.reference.relatedIngredient?.lead}
							href={`${InternalLink.Ingredient}${ref.reference.relatedIngredient?.slug}`}
							imageUrl={ref.reference.relatedIngredient?.image?.url}
							positiveTags={
								ref.reference.relatedIngredient?.positiveForBodyTags
							}
							negativeTags={
								ref.reference.relatedIngredient?.negativeForBodyTags
							}
						/>
					);
				},
				relatedBrand(ref) {
					return (
						ref.reference.relatedBrand && (
							<BrandBanner
								badge={ref.reference.badge}
								badgeColor={ref.reference.badgeColor}
								isEmbedded
								noBottomBorder
								brandId={ref.reference.relatedBrand.id}
								title={ref.reference.relatedBrand.name ?? "Značka"}
								buttonLabel={`${translate("listingBrands.title.multiple")} (${
									ref.reference.relatedBrand.paginateProducts.pageInfo
										.totalCount
								})`}
								lead={ref.reference.relatedBrand.lead}
								href={`${InternalLink.Brand}${ref.reference.relatedBrand.slug}`}
								imageUrl={ref.reference.relatedBrand.logo?.url}
								tagContent={
									selectFlavors(ref.reference.relatedBrand).length > 0 ? (
										<TagRow tags={selectFlavors(ref.reference.relatedBrand)} />
									) : undefined
								}
							/>
						)
					);
				},
				relatedBrandFull(ref) {
					if (!ref.reference.relatedBrandFull) {
						return null;
					}
					interface ProductLine {
						paginateProducts?: {
							pageInfo?: {
								totalCount: number;
							};
						};
					}
					const brand = ref.reference.relatedBrandFull;
					const notNullProductLines = brand.productLines?.filter(
						(line: ProductLine) =>
							line.paginateProducts &&
							line.paginateProducts.pageInfo &&
							line.paginateProducts.pageInfo.totalCount > 0
					);

					return (
						ref.reference.relatedBrandFull && (
							<div
								id={`${slugify(brand.name)}-detail`}
								className={styles.relatedBrandFull}
							>
								{ref.reference.primaryText && <h3 className={styles.fullTitle} id={slugify(ref.reference.primaryText)}>{ref.reference.primaryText}</h3>}
								<BrandBanner
									badge={ref.reference.badge}
									badgeColor={ref.reference.badgeColor}
									brandId={brand.id}
									isEmbedded
									title={brand.name ?? "Značka"}
									buttonLabel={`${translate("listingBrands.title.multiple")} (${
										brand.paginateProducts.pageInfo.totalCount
									})`}
									noBottomBorder
									lead={brand.lead}
									href={`${InternalLink.Brand}${brand.slug}`}
									imageUrl={brand.logo?.url}
									tagContent={
										selectFlavors(brand).length > 0 ? (
											<TagRow tags={selectFlavors(brand)} />
										) : undefined
									}
								/>
								{notNullProductLines && notNullProductLines.length > 0 && (
									<ProductLineCardGroup
										productLines={notNullProductLines}
										title={translate("brandPage.productLines.title")}
									/>
								)}
								<ManufacturerArticle
									manufacturer={
										brand.manufacturers?.length
											? brand.manufacturers.map((m) => (
													<span key={m.id}>{m.name}</span>
											  ))
											: undefined
									}
									distributor={
										brand.distributor ? (
											<span>{brand.distributor.name}</span>
										) : undefined
									}
									residence={
										brand.manufacturers?.length
											? brand.manufacturers.map((m) => (
													<span key={m.id}>
														{m.residence?.region?.name || ""}
													</span>
											  ))
											: undefined
									}
									countriesOfProduction={
										brand.countriesOfProduction?.length
											? brand.countriesOfProduction.map((c) => (
													<span key={c.id}>{c.name}</span>
											  ))
											: undefined
									}
								/>
								{/* <div className={styles.buttonWrapper}>
									<HeurekaButton
										id={brand.id}
										link={brand.}
									/>
								</div> */}
								<div />
							</div>
						)
					);
				},
				relatedProductFull(ref) {
					const product = ref.reference.relatedProductFull;
					if (!product) return null;

					const features: Array<ProductFeatureResult> = [];

					const elementsFeatures = product.elements.flatMap((e) => {
						if (e.amount !== undefined && e.amount > 0) {
							return (
								e.element?.featureDependencies.filter(
									(f) =>
										f.value === FeatureValueType.one ||
										f.value === FeatureValueType.moreThanZero
								) ?? []
							);
						}
						if (e.amount === 0) {
							return (
								e.element?.featureDependencies.filter(
									(f) => f.value === FeatureValueType.zero
								) ?? []
							);
						}
						return [];
					});
					features.push(...elementsFeatures);

					features.push(
						...product.itemSubCategories.flatMap(
							(s) => s.featureDependencies ?? []
						)
					);
					features.push(
						...product.itemCategories.flatMap(
							(c) => c.featureDependencies ?? []
						)
					);

					const productFeatures = product.flags.flatMap((f) => {
						if (f.value === ProductFlagValue.minusOne) {
							return (
								f.flag?.productFeatures.filter(
									(pf) => pf.value === FeatureValueType.minusOne
								) ?? []
							);
						}
						return [];
					});
					features.push(...productFeatures);

					const positiveFeatures = features?.filter(
						(feature) => feature.category === FeatureCategoryType.positive
					);
					const negativeFeatures = features?.filter(
						(feature) =>
							feature.category === FeatureCategoryType.negative &&
							feature.value !== "minusOne"
					);

					return (
						props.allAllergens && (
							<div
								id={`${slugify(product.name)}-detail`}
								className={styles.relatedProductFull}
							>
								{ref.reference.primaryText && <h3 className={styles.fullTitle} id={slugify(ref.reference.primaryText)}>{ref.reference.primaryText}</h3>}
								<ProductBanner
									badge={ref.reference.badge}
									badgeColor={ref.reference.badgeColor}
									isEmbedded
									noBottomBorder
									openModal={props.openModal}
									dogDailyEnergyRequirement={dogDailyEnergyRequirementNumber}
									product={product as any}
									excludedAllergens={getExcludedAllergens(
										props.allAllergens,
										product as any
									)}
								/>
								<SummaryArticle
									production={product.brand?.methodOfProduction?.name || "N/A"}
									isDiet={!!product.isVeterinaryDiet}
									firstFiveItems={product.firstFiveItems}
									vetDietInfo={[
										{
											id: "vetDietInfo",
											json: product.isVeterinaryDiet
												? "veterinární dieta"
												: "bežné krmivo",
											order: 0,
										},
									]}
									size={
										product.granuleSizes && product.granuleSizes.length > 0 ? (
											<span>
												{rephraseSize(
													product.granuleSizes.map((s: any) => s.name)
												)}
											</span>
										) : undefined
									}
									ingredientCategories={
										product.itemCategories && product.itemCategories.length > 0
											? product.itemCategories.map((category: any) => (
													<Link
														key={category.name}
														href={`${InternalLink.IngredientCategory}${category.slug}`}
														className={styles.categoryLink}
														target="_blank"
													>
														{category.name}
													</Link>
											  ))
											: undefined
									}
								/>

								{product.nutritionalValues &&
									product.nutritionalValues.length > 0 && (
										<div className={styles.nutritionValuesWrapper}>
											<IconItemGroup
												title={translate("summaryTab.nutritionalValues.title")}
												items={product.nutritionalValues}
											/>
										</div>
									)}

								<div className={styles.features}>
									<div className={styles.feature}>
										{/* {positiveFeatures.length > 0 && ( */}
										<SummaryList
											small
											summaryCategory={{
												title: "Líbí se nám:",
												id: "positive-features",
												icon: (
													<Image
														className={styles.img}
														src={thumbUp}
														alt="Thump up"
													/>
												),
												values: positiveFeatures,
											}}
										/>
										{/* )} */}
									</div>

									<div className={styles.feature}>
										{negativeFeatures.length > 0 && (
											<SummaryList
												small
												summaryCategory={{
													title: "Nelíbí se nám:",
													icon: (
														<Image
															className={styles.img}
															src={thumbDown}
															alt="Thump down"
														/>
													),
													id: "negative-features",
													values: negativeFeatures,
												}}
											/>
										)}
									</div>
								</div>
							</div>
						)
					);
				},
				list(ref) {
					if (!ref.reference) {
						return null;
					}

					return (
						<>
							{ref.reference.primaryText && (
								<div className={styles.body}>
									<h2
										id={slugify(ref.reference.primaryText)}
										className={styles.title}
									>
										{ref.reference.primaryText}
									</h2>
									<div className={styles.paragraph}>
										{ref.reference.secondaryText}
									</div>
								</div>
							)}
							<AccordionList
								translate={translate}
								defaultOpen={ref.reference.defaultOpen}
							>
								{ref.reference.list.map((item, index) => {
									return (
										<div key={index}>
											{item.relatedBrand && (
												<BrandBanner
													badge={item.badge}
													badgeColor={item.badgeColor}
													isEmbedded
													showDetailButton={true}
													brandId={item.relatedBrand.id}
													title={item.relatedBrand.name ?? "Značka"}
													buttonLabel={`${translate(
														"listingBrands.title.multiple"
													)} (${
														item.relatedBrand.paginateProducts.pageInfo
															.totalCount
													})`}
													lead={item.relatedBrand.lead}
													href={`${InternalLink.Brand}${item.relatedBrand.slug}`}
													imageUrl={item.relatedBrand.logo?.url}
													tagContent={
														selectFlavors(item.relatedBrand).length > 0 ? (
															<TagRow tags={selectFlavors(item.relatedBrand)} />
														) : undefined
													}
												/>
											)}
											{item.relatedProduct && props.allAllergens && (
												<ProductBanner
													badge={item.badge}
													badgeColor={item.badgeColor}
													isEmbedded
													showDetailButton={true}
													openModal={props.openModal}
													dogDailyEnergyRequirement={
														dogDailyEnergyRequirementNumber
													}
													product={item.relatedProduct}
													excludedAllergens={getExcludedAllergens(
														props.allAllergens,
														item.relatedProduct
													)}
												/>
											)}
										</div>
									);
								})}
							</AccordionList>
						</>
					);
				},
				richtext() {
					// TODO: RichText
					return null;
				},
				ad(ref) {
					const ad = ref.reference?.ad;
					if (!ad) {
						return null;
					}

					if (ad.slug === "generated") {
						return <RegisterBanner />;
					}

					return null;

					// const adImage = ad?.image
					// if (!adImage) {
					// 	return null
					// }
					// return (
					// 	<div
					// 		styles={{
					// 			position: 'relative',
					// 			width: '100%',
					// 			minHeight: '260px',
					// 			height: 'auto',
					// 		}}
					// 	>
					// 		<Link href={ad.link} key={ad.link} title={adImage.alt} className={styles.adWrapper}>
					// 			<Image src={adImage.url} alt={adImage.alt ?? ''} title={adImage.alt} layout="fill" sizes="260px" />
					// 		</Link>
					// 	</div>
					// )
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			dogDailyEnergyRequirementNumber,
			flattenMedia,
			lightBox,
			props.allAllergens,
			props.openModal,
			translate,
		]
	);

	return useMemo(
		() => (
			<>
				<RichTextRenderer
					blocks={props.blocks}
					sourceField="json"
					renderBlock={(props: {
						block: unknown;
						children?: React.ReactNode;
					}) => {
						return <div className={styles.contentstyless}>{props.children}</div>;
					}}
					renderLeaf={(props) => {
						if (
							(props.leaf as unknown as { isSuperscript: boolean })
								.isSuperscript
						) {
							return <sup>{props.fallback}</sup>;
						}
						if (
							(props.leaf as unknown as { isSubscript: boolean }).isSubscript
						) {
							return <sub>{props.fallback}</sub>;
						}

						return props.fallback;
					}}
					renderElement={(props) => {
						const type = props.element.type;
						if (type === "anchor") {
							return (
								<a
									href={props.element.href}
									title={
										(props.element.children[0] as unknown as { text: string })
											.text
									}
									rel="noreferrer"
								>
									{props.children}
								</a>
							);
						}
						if (nestedTypes.includes(type)) {
							const reference = props.reference as ContentReferenceResult;
							if (
								props.referenceType === "link" &&
								reference.link?.externalLink === "#kontaktni-formular"
							) {
								return (
									<button
										className={styles.contactButton}
										style={{ marginLeft: "4px" }}
										onClick={() => setIsContactModalOpen(true)}
									>
										{props.children}
									</button>
								);
							}
							if (props.referenceType === "link" && reference.link) {
								return (
									reference.link.externalLink && (
										<Link
											href={reference.link.externalLink}
											title={reference.link.externalLink}
											target={reference.link.isTargetBlank ? "_blank" : ""}
										>
											{props.children}
										</Link>
									)
								);
							}

							return props.fallback;
						}
						return props.fallback;
					}}
					referenceRenderers={referenceRenderers}
				/>
				{isContactModalOpen && (
					<ContactFormMessageModal
						isModalOpen={isContactModalOpen}
						close={() => setIsContactModalOpen(false)}
					/>
				)}
			</>
		),
		[isContactModalOpen, nestedTypes, props.blocks, referenceRenderers]
	);
}
